import { useEffect, useMemo, useState } from 'react';
import { api, type DashboardOverview } from '@/api/client';
import { mockOverview, useMock } from '@/api/mock';

interface AssetRiskRow {
  assetName: string;
  owner: string;
  region: string;
  exposure: 'Public' | 'Internal' | 'Restricted';
  findingCount: number;
}

const mockAssets: AssetRiskRow[] = [
  { assetName: 'vm-prod-ai-01', owner: 'Platform Ops', region: 'eastus2', exposure: 'Public', findingCount: 6 },
  { assetName: 'stcontosopii', owner: 'Data Governance', region: 'westeurope', exposure: 'Restricted', findingCount: 4 },
  { assetName: 'aks-ai-prod', owner: 'ML Engineering', region: 'southeastasia', exposure: 'Internal', findingCount: 3 },
  { assetName: 'kv-core-prod', owner: 'Security', region: 'eastus', exposure: 'Restricted', findingCount: 1 },
];

function severityWeight(severity: string) {
  switch (severity) {
    case 'Critical': return 10;
    case 'High': return 5;
    case 'Medium': return 2;
    case 'Low': return 0.5;
    default: return 0;
  }
}

export function SecurityPosturePage() {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);

  useEffect(() => {
    if (useMock()) {
      setOverview(mockOverview);
      return;
    }
    api.dashboardOverview().then(setOverview).catch(() => setOverview(mockOverview));
  }, []);

  const recommendations = useMemo(() => {
    if (!overview) return [] as string[];
    const critical = overview.vulnerabilities.filter(v => v.severity === 'Critical').length;
    const high = overview.vulnerabilities.filter(v => v.severity === 'High').length;
    const weighted = overview.vulnerabilities.reduce((sum, v) => sum + severityWeight(v.severity), 0);
    const res: string[] = [];
    if (critical > 0) res.push('Remediate Critical findings immediately. Complete containment and retest within 24 hours.');
    if (high >= 2) res.push('High-risk items are elevated. Prioritize remediation by asset exposure and blast radius.');
    if (weighted > 15) res.push('Aggregate risk is high. Freeze high-risk changes and start focused remediation sprint.');
    if (res.length === 0) res.push('Risk posture is stable. Maintain weekly baseline reviews and policy checks.');
    return res;
  }, [overview]);

  if (!overview) return <div className="p-8 text-ink-muted">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Security Posture</h1>
        <p className="text-sm text-ink-muted mt-1">Full vulnerability register, critical asset risk, and remediation recommendations.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-title">Security Score</div>
          <div className="kpi-value">{overview.securityScore.toFixed(1)}</div>
          <div className="text-xs text-ink-muted mt-1">30-day trend +{overview.scoreTrendDays30}</div>
        </div>
        <div className="card">
          <div className="card-title">Critical Findings</div>
          <div className="kpi-value text-bad">{overview.vulnerabilities.filter(v => v.severity === 'Critical').length}</div>
        </div>
        <div className="card">
          <div className="card-title">Open Findings</div>
          <div className="kpi-value text-warn">{overview.vulnerabilities.filter(v => v.status === 'Open').length}</div>
        </div>
        <div className="card">
          <div className="card-title">MFA Coverage</div>
          <div className="kpi-value text-good">{overview.rbac.mfaCoveragePct}%</div>
        </div>
      </div>

      <div className="card overflow-auto">
        <div className="card-title mb-3">Vulnerability Detail</div>
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-ink-muted border-b border-border">
            <tr>
              <th className="text-left py-2">Severity</th>
              <th className="text-left py-2">Finding</th>
              <th className="text-left py-2">Asset</th>
              <th className="text-left py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {overview.vulnerabilities.map((v, i) => (
              <tr key={`${v.assetName}-${i}`} className="border-b border-border/50 last:border-0">
                <td className="py-2"><span className={`severity-${v.severity}`}>{v.severity}</span></td>
                <td className="py-2">{v.finding}</td>
                <td className="py-2">{v.assetName}</td>
                <td className="py-2">{v.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card overflow-auto">
          <div className="card-title mb-3">Asset Risk Register</div>
          <table className="w-full text-sm min-w-[520px]">
            <thead className="text-ink-muted border-b border-border">
              <tr>
                <th className="text-left py-2">Asset</th>
                <th className="text-left py-2">Owner</th>
                <th className="text-left py-2">Region</th>
                <th className="text-left py-2">Exposure</th>
                <th className="text-left py-2">Findings</th>
              </tr>
            </thead>
            <tbody>
              {mockAssets.map((a) => (
                <tr key={a.assetName} className="border-b border-border/50 last:border-0">
                  <td className="py-2">{a.assetName}</td>
                  <td className="py-2">{a.owner}</td>
                  <td className="py-2">{a.region}</td>
                  <td className="py-2">{a.exposure}</td>
                  <td className="py-2">{a.findingCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-title mb-3">Remediation Recommendations</div>
          <ul className="space-y-2 text-sm text-ink-muted">
            {recommendations.map((r, i) => <li key={i}>- {r}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}
